// @givmo/board — the connect flow as a hook: start, the consent window, the return, complete
// (CBG-CAL1 slice 1d). The shape and its reasons are `calendar-connect.ts`'s header.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useBoardBasePath, useBoardConnectionOperations } from '../board-runtime';
import {
  CALENDAR_CONNECT_CHANNEL,
  calendarConnectRedirectUri,
  openConsentWindow,
  outcomeOfCompleteFailure,
  outcomeOfProviderError,
  readConnectMessage,
  startBody,
  stateOf,
  type ConnectOutcome,
  type ConnectRequest,
} from './calendar-connect';
import { useRefreshCalendar } from './calendar-queries';

/** This tab's key for the page a full-page trip to the provider goes back to. */
export const CONNECT_RETURN_PATH_KEY = 'givmo.board.calendar-connect-return';

export interface CalendarConnectFlow {
  /**
   * Begin a connect: call it INSIDE the click, so the consent window opens on the person's own
   * gesture. `beforeRedirect` runs only when the whole tab must go (no window): keep what the page
   * would lose.
   */
  begin: (
    request: Omit<ConnectRequest, 'orgRef' | 'returnPath'>,
    options?: { beforeRedirect?: () => void },
  ) => void;
  /** Starting: `start` is in flight. */
  starting: boolean;
  /** The answer, once there is one; null before and after `reset`. */
  outcome: ConnectOutcome | null;
  /** Forget the answer (Try again, Close). */
  reset: () => void;
}

/**
 * The console page that asked, as the address bar has it when the person clicks: where a full-page
 * trip to the provider comes back to. Read at the click, not from a router, so a card mounted
 * anywhere (a settings panel, a dialog) asks the same way.
 *
 * Its query keeps every slash plain (CBG-FIX191): a `/` needs no encoding in a query, and core
 * refuses an encoded one anywhere in a return path (`invalid_return_path`), so Settings opened from
 * Board's menu (`/settings?from=%2Fboard%2F…`) once failed every connect. The query stays, so a
 * full-page trip still comes back to Board's section of Settings.
 */
function currentPath(): string {
  const location = globalThis.location;
  if (location === undefined) return '/';
  return `${location.pathname}${location.search.replace(/%2F/gi, '/')}`;
}

function rememberReturnPath(path: string): void {
  try {
    globalThis.sessionStorage?.setItem(CONNECT_RETURN_PATH_KEY, path);
  } catch {
    // Storage refused: the return page's Done goes to Board's meetings instead.
  }
}

/** The flow for one organization. `onConnected` runs after a connection lands (refresh, close…). */
export function useCalendarConnect(
  orgRef: string | null,
  onConnected?: (outcome: ConnectOutcome) => void,
): CalendarConnectFlow {
  const connections = useBoardConnectionOperations();
  const basePath = useBoardBasePath();
  const refresh = useRefreshCalendar(orgRef);
  const [starting, setStarting] = useState(false);
  const [outcome, setOutcome] = useState<ConnectOutcome | null>(null);
  const channel = useRef<BroadcastChannel | null>(null);
  const expected = useRef<string | null>(null);
  const settled = useRef(onConnected);
  settled.current = onConnected;

  const stopListening = useCallback(() => {
    channel.current?.close();
    channel.current = null;
    expected.current = null;
  }, []);
  useEffect(() => stopListening, [stopListening]);

  const finish = useCallback(
    async (state: string, code: string) => {
      if (orgRef === null) return;
      let answer: ConnectOutcome;
      try {
        const { data } = await connections.completeOrgIntegrationOauth({
          path: { org_ref: orgRef },
          body: { state, code, redirect_uri: calendarConnectRedirectUri(basePath) },
          throwOnError: true,
        });
        answer = { kind: 'connected', connection: data };
      } catch (error) {
        answer = outcomeOfCompleteFailure(error);
      }
      setOutcome(answer);
      if (answer.kind === 'connected') {
        await refresh();
        settled.current?.(answer);
      }
    },
    [basePath, connections, orgRef, refresh],
  );

  const listen = useCallback(
    (state: string) => {
      stopListening();
      const opened = new BroadcastChannel(CALENDAR_CONNECT_CHANNEL);
      opened.onmessage = (event: MessageEvent) => {
        const message = readConnectMessage(event.data);
        if (message?.type !== 'return' || message.state !== expected.current) return;
        // Ours: answer, so the window closes itself, and finish here, in the same dialog.
        opened.postMessage({ type: 'ack', state: message.state });
        stopListening();
        if (message.error !== null) setOutcome(outcomeOfProviderError(message.error));
        else if (message.code !== null) void finish(message.state, message.code);
      };
      channel.current = opened;
      expected.current = state;
    },
    [finish, stopListening],
  );

  const begin = useCallback<CalendarConnectFlow['begin']>(
    (request, options) => {
      if (orgRef === null) return;
      const consent = openConsentWindow();
      const returnPath = currentPath();
      setOutcome(null);
      setStarting(true);
      void (async () => {
        try {
          const { data } = await connections.startOrgIntegrationOauth({
            path: { org_ref: orgRef },
            body: startBody(
              { ...request, orgRef, returnPath },
              calendarConnectRedirectUri(basePath),
            ),
            throwOnError: true,
          });
          const url = data.authorization_url;
          const state = stateOf(url);
          if (consent === null || state === null) {
            consent?.close();
            options?.beforeRedirect?.();
            rememberReturnPath(returnPath);
            window.location.assign(url);
            return;
          }
          listen(state);
          consent.location.replace(url);
        } catch (error) {
          consent?.close();
          setOutcome(outcomeOfCompleteFailure(error));
        } finally {
          setStarting(false);
        }
      })();
    },
    [basePath, connections, listen, orgRef],
  );

  const reset = useCallback(() => setOutcome(null), []);
  return { begin, starting, outcome, reset };
}
